import { useState, useMemo } from "react";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import DataTable from "cxfcomponents/Tables/DataTable";
import Checkbox from "@mui/material/Checkbox";
import StatusBadge from "cxfcomponents/StatusBadge";
import SoftSelect from "components/SoftSelect";
import SoftButton from "components/SoftButton";
import SoftTypography from "components/SoftTypography";
import ProductCellfromUser from "layouts/pages/users/reports/components/Reviews/ProductCell";
import SoftBox from "components/SoftBox";
import { useAuth0 } from "@auth0/auth0-react";
import "react-toastify/dist/ReactToastify.css";
import { useSelector } from "react-redux";
import { useRoles } from "hooks/useRoles";
import { CircularProgress } from "@mui/material";
import { deleteSharedConnectors } from "api/ComponentsApi";
import { toast } from "react-toastify";

export const UnShareConnectorDialog = ({ open, onClose, component }) => {
	const { user } = useAuth0();
	const [usersToUnshare, setUsersToUnshare] = useState([]);
	const [selectAllChecked, setSelectAllChecked] = useState(false);
	const { users } = useSelector((store) => store?.portal?.users);
	const { sharedConnectors } = useSelector(
		(store) => store?.portal?.connectors
	);
	const { roles } = useRoles(false);
	const [isSubmitting, setIsSubmitting] = useState(false);

	const handleClose = () => {
		onClose();
		setSelectAllChecked(false);
		setUsersToUnshare([]);
	};

	const handleSelectedUser = (userData, isChecked) => {
		const { id } = userData;

		if (isChecked) {
			setUsersToUnshare((prev) => [
				...prev,
				{
					connector_id: component.id,
					shared_user_id: id
				}
			]);

			if (getUsersToUnshare().length === usersToUnshare.length + 1)
				setSelectAllChecked(true);

			return;
		}

		if (selectAllChecked && !isChecked) {
			setSelectAllChecked(false);
		}

		setUsersToUnshare((prev) =>
			prev.filter((user) => !(user.shared_user_id === id))
		);
	};

	const isChecked = (userId) =>
		usersToUnshare.some(({ shared_user_id }) => shared_user_id === userId);

	const handleUnshareConnector = async () => {
		try {
			setIsSubmitting(true);
			const mappedSharedConnectorIds = sharedConnectors
				.filter(
					(shared) =>
						shared.connector_id === component.id &&
						usersToUnshare.some(
							(unshare) =>
								unshare.shared_user_id === shared.shared_user_id
						)
				)
				.map((shared) => shared.id);

			const deleteSharedConnectorsResponse = await deleteSharedConnectors(
				user?.stripeId,
				mappedSharedConnectorIds
			);

			handleClose();
			if (deleteSharedConnectorsResponse.resultObject.length > 0) {
				toast.success(
					`Connector ${component.name} has been unshared successfully!`
				);
			} else {
				toast.warning(`Unshared failed. No users selected`);
			}
		} catch (error) {
			console.log(error);
		} finally {
			setIsSubmitting(false);
		}
	};

	const toggleSelectAll = () => {
		const selected = !selectAllChecked;
		setSelectAllChecked(selected);

		if (selected) {
			setUsersToUnshare(
				getUsersToUnshare().map((userToShare) => ({
					connector_id: component.id,
					shared_user_id: userToShare.auth0_id,
					access_type: "readOnly",
					tenant_id: user?.stripeId
				}))
			);
		} else {
			setUsersToUnshare([]);
		}
	};

	const getUsersToUnshare = () => {
		const filteredUsers = sharedConnectors
			.filter(({ connector_id }) => connector_id === component.id)
			.map(({ shared_user_id }) => ({
				...users.find((user) => user.auth0_id === shared_user_id)
			}));

		return filteredUsers;
	};

	const dataTableData = useMemo(() => {
		return {
			columns: [
				{
					Header: "share to",
					accessor: "selectUser",
					canGroupBy: false,
					disableSortBy: true,
					disableGlobalFilter: true,
					Cell: ({ value }) => {
						return (
							<Checkbox
								checked={isChecked(value.id)}
								onChange={(event) =>
									handleSelectedUser(value, event.target.checked)
								}
							/>
						);
					}
				},
				{
					Header: "users",
					accessor: "users",
					width: 500,
					disableSortBy: false,
					canGroupBy: false,
					disableGlobalFilter: true,
					defaultSortOrder: "desc"
				},
				{
					Header: "name",
					accessor: "name",
					show: false
				},
				{
					Header: "status",
					accessor: "status",
					Cell: ({ value }) => {
						return <StatusBadge status={value} />;
					},
					canGroupBy: false,
					disableGlobalFilter: true
				},
				{
					Header: "access type",
					accessor: "accessType",
					canGroupBy: false,
					disableSortBy: true,
					disableGlobalFilter: true,
					Cell: ({ value }) => {
						const { id } = value;

						return (
							<SoftBox
								width="250px"
								className="inline-select-wrapper"
							>
								<SoftSelect
									key={id}
									isDisabled={true}
									options={[
										{ value: "readOnly", label: "Read Only" },
										{ value: "editor", label: "Editor" }
									]}
									defaultValue={{
										value: sharedConnectors.find(
											({ connector_id, shared_user_id }) =>
												connector_id === component.id &&
												shared_user_id === id
										)?.access_type,
										label:
											sharedConnectors.find(
												({ connector_id, shared_user_id }) =>
													connector_id === component.id &&
													shared_user_id === id
											)?.access_type === "readOnly"
												? "Read Only"
												: "Editor"
									}}
								/>
							</SoftBox>
						);
					}
				}
			],
			rows: getUsersToUnshare().map((item) => {
				return {
					selectUser: {
						id: item.auth0_id,
						name: `${item.first_name} ${item.last_name}`
					},
					users: (
						<ProductCellfromUser
							id={item.auth0_id}
							image={item.image}
							name={`${item.first_name} ${item.last_name}`}
							firstName={item.first_name}
							lastName={item.last_name}
							email={item.email_address}
							checked={true}
						/>
					),
					name: `${item.first_name} ${item.last_name}`,
					role: (() => {
						if (roles?.length !== 0) {
							if (!item.role_id) {
								return "";
							} else {
								const roleName = roles?.find(
									(role) => role.id === item.role_id
								)?.name;
								return roleName ? roleName : "";
							}
						} else {
							return "";
						}
					})(),
					lastActive: "",
					status: item?.status,
					accessType: {
						id: item.auth0_id,
						name: `${item.first_name} ${item.last_name}`
					}
				};
			})
		};
	}, [
		roles,
		users,
		selectAllChecked,
		component,
		sharedConnectors,
		usersToUnshare
	]);

	return (
		<Dialog
			fullWidth
			open={open}
			onClose={handleClose}
			PaperProps={{
				style: {
					maxWidth: "1020px"
				}
			}}
		>
			<DialogContent className="share-flow-content-index-2">
				<DialogTitle sx={{ marginLeft: "8px" }}>
					Unshare Connector: {component.name}
				</DialogTitle>
				<SoftBox
					width={{ xs: "100%" }}
					maxWidth={{ xl: "100px" }}
					sx={{ marginLeft: "24px" }}
				>
					<SoftTypography
						variant="h6"
						mb={1}
					>
						<Checkbox
							checked={selectAllChecked}
							onChange={toggleSelectAll}
						/>
						Select All
					</SoftTypography>
				</SoftBox>
				<DataTable
					table={dataTableData}
					hiddenTopButtons={true}
					flowView="list"
					withSearchbar={true}
				/>
			</DialogContent>
			<DialogActions>
				<SoftButton
					variant="outlined"
					color="info"
					onClick={handleClose}
				>
					Close
				</SoftButton>
				<SoftButton
					color="info"
					onClick={handleUnshareConnector}
					disabled={!(usersToUnshare?.length > 0)}
				>
					{isSubmitting ? (
						<CircularProgress
							size={24}
							sx={{
								color: "#fff",
								position: "absolute",
								top: "50%",
								left: "50%",
								marginTop: "-12px",
								marginLeft: "-12px"
							}}
						/>
					) : (
						"Unshare"
					)}
				</SoftButton>
			</DialogActions>
		</Dialog>
	);
};
